import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";

export default function PrestationsRecusList() {
  const [recus, setRecus] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [deviseFilter, setDeviseFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const limit = 20;

  const navigate = useNavigate();

  // ============================================================
  // CHARGEMENT
  // ============================================================

  useEffect(() => {
    loadRecus();
  }, []);

  const loadRecus = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/blanchisserie");

      /*
       * L'API blanchisserie renvoie :
       *
       * {
       *   data: [...]
       * }
       */

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setRecus(data);
    } catch (err) {
      console.error(
        "Erreur chargement reçus prestations:",
        err
      );

      setError(
        err?.response?.data?.error ||
          "Impossible de charger les reçus des prestations."
      );

      setRecus([]);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // OUVRIR LE REÇU
  // ============================================================

  const openRecu = (id) => {
    navigate(`/blanchisserie/${id}`);
  };

  // ============================================================
  // DATE DU JOUR
  // ============================================================

  const today = new Date();

  // ============================================================
  // FILTRAGE
  // ============================================================

  const filtered = useMemo(() => {
    return recus.filter((r) => {
      const numero =
        r.numero || "";

      const client =
        r.client_nom ||
        r.client_nom_fiche ||
        "";

      const encaisseur =
        r.encaisseur_nom ||
        "";

      const texte =
        `
        ${numero}
        ${client}
        ${encaisseur}
        ${r.mode_paiement || ""}
        `
          .toLowerCase()
          .trim();

      const matchSearch =
        texte.includes(
          search.toLowerCase().trim()
        );

      const dateValue =
        r.date_encaissement ||
        r.created_at;

      const date = dateValue
        ? new Date(dateValue)
        : null;

      // --------------------------------------------------------
      // FILTRE AUJOURD'HUI
      // --------------------------------------------------------

      if (filter === "today") {
        return (
          matchSearch &&
          date &&
          date.toDateString() ===
            today.toDateString() &&
          (
            deviseFilter === "all" ||
            r.devise === deviseFilter
          )
        );
      }

      // --------------------------------------------------------
      // FILTRE 7 JOURS
      // --------------------------------------------------------

      if (filter === "week") {
        const diff = date
          ? (
              today - date
            ) /
            (
              1000 *
              60 *
              60 *
              24
            )
          : 999;

        return (
          matchSearch &&
          diff >= 0 &&
          diff <= 7 &&
          (
            deviseFilter === "all" ||
            r.devise === deviseFilter
          )
        );
      }

      // --------------------------------------------------------
      // FILTRE DEVISE
      // --------------------------------------------------------

      return (
        matchSearch &&
        (
          deviseFilter === "all" ||
          r.devise === deviseFilter
        )
      );
    });
  }, [
    recus,
    search,
    filter,
    deviseFilter
  ]);

  // ============================================================
  // PAGINATION
  // ============================================================

  const totalPages =
    Math.ceil(
      filtered.length /
        limit
    );

  const paginated =
    filtered.slice(
      (page - 1) * limit,
      page * limit
    );

  // ============================================================
  // STATISTIQUES
  // ============================================================

  const totalUSD =
    filtered.reduce(
      (sum, r) =>
        sum +
        (
          r.devise === "USD"
            ? Number(
                r.montant || 0
              )
            : 0
        ),
      0
    );

  const totalCDF =
    filtered.reduce(
      (sum, r) =>
        sum +
        (
          r.devise === "CDF"
            ? Number(
                r.montant || 0
              )
            : 0
        ),
      0
    );

  // ============================================================
  // FORMAT MONTANT
  // ============================================================

  const formatMontant = (
    montant,
    devise
  ) => {
    return `${Number(
      montant || 0
    ).toLocaleString(
      "fr-FR",
      {
        minimumFractionDigits:
          2,
        maximumFractionDigits:
          2
      }
    )} ${devise || ""}`;
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDate = (
    value
  ) => {
    if (!value) {
      return "-";
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "-";
    }

    return date.toLocaleDateString(
      "fr-FR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      }
    );
  };

  // ============================================================
  // FORMAT HEURE
  // ============================================================

  const formatHeure = (
    value
  ) => {
    if (!value) {
      return "";
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "";
    }

    return date.toLocaleTimeString(
      "fr-FR",
      {
        hour: "2-digit",
        minute: "2-digit"
      }
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 min-h-[100dvh] bg-gray-100">

      {/* ======================================================
          HEADER
      ======================================================= */}

      <div className="flex flex-col md:flex-row md:justify-between gap-3">

        <div>
          <h2 className="text-2xl md:text-3xl font-semibold">
            Reçus des prestations
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Encaissements des prestations
          </p>
        </div>

        <input
          type="text"
          placeholder="Rechercher..."
          className="border px-4 py-3 rounded-xl w-full md:w-72 bg-white outline-none focus:ring-2 focus:ring-green-400"
          value={search}
          onChange={(e) => {
            setSearch(
              e.target.value
            );

            setPage(1);
          }}
        />

      </div>


      {/* ======================================================
          FILTRES
      ======================================================= */}

      <div className="flex gap-2 flex-wrap">

        <button
          onClick={() => {
            setFilter("all");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            filter === "all"
              ? "bg-gray-800 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          Tous
        </button>


        <button
          onClick={() => {
            setFilter("today");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            filter === "today"
              ? "bg-blue-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          Aujourd’hui
        </button>


        <button
          onClick={() => {
            setFilter("week");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            filter === "week"
              ? "bg-green-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          7 jours
        </button>


        {/* ==================================================
            DEVISES
        =================================================== */}

        <button
          onClick={() => {
            setDeviseFilter("all");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            deviseFilter === "all"
              ? "bg-gray-800 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          Toutes devises
        </button>


        <button
          onClick={() => {
            setDeviseFilter("USD");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            deviseFilter === "USD"
              ? "bg-blue-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          USD
        </button>


        <button
          onClick={() => {
            setDeviseFilter("CDF");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            deviseFilter === "CDF"
              ? "bg-green-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          CDF
        </button>

      </div>


      {/* ======================================================
          ERREUR
      ======================================================= */}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
          {error}
        </div>
      )}


      {/* ======================================================
          STATISTIQUES
      ======================================================= */}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">

        <div className="bg-white p-4 rounded-2xl shadow text-center">

          <p className="text-sm text-gray-500">
            Total USD
          </p>

          <p className="font-bold text-lg md:text-xl mt-1">
            {totalUSD.toLocaleString(
              "fr-FR",
              {
                minimumFractionDigits:
                  2,
                maximumFractionDigits:
                  2
              }
            )} $
          </p>

        </div>


        <div className="bg-white p-4 rounded-2xl shadow text-center">

          <p className="text-sm text-gray-500">
            Total CDF
          </p>

          <p className="font-bold text-lg md:text-xl mt-1">
            {totalCDF.toLocaleString(
              "fr-FR",
              {
                minimumFractionDigits:
                  2,
                maximumFractionDigits:
                  2
              }
            )} FC
          </p>

        </div>


        <div className="bg-green-100 p-4 rounded-2xl text-center">

          <p className="text-sm">
            Nombre de reçus
          </p>

          <p className="font-bold text-green-700 text-lg md:text-xl mt-1">
            {filtered.length}
          </p>

        </div>

      </div>


      {/* ======================================================
          LOADING
      ======================================================= */}

      {loading && (
        <div className="bg-white rounded-2xl shadow p-8 text-center text-gray-500">
          Chargement des reçus...
        </div>
      )}


      {!loading && !error && (
        <>

          {/* ==================================================
              MOBILE
          =================================================== */}

          <div className="grid gap-3 md:hidden">

            {paginated.length === 0 && (
              <div className="bg-white p-6 rounded-2xl shadow text-center text-gray-500">
                Aucun reçu trouvé.
              </div>
            )}


            {paginated.map((r) => {

              const dateValue =
                r.date_encaissement ||
                r.created_at;

              const client =
                r.client_nom ||
                r.client_nom_fiche ||
                "Client de passage";

              const encaisseur =
                r.encaisseur_nom ||
                "Utilisateur non identifié";

              return (
                <div
                  key={r.id}
                  className="bg-white p-4 rounded-2xl shadow border cursor-pointer hover:bg-gray-50 transition"
                  onClick={() =>
                    openRecu(r.id)
                  }
                >

                  <div className="flex justify-between items-start gap-2">

                    <span className="font-semibold text-green-600 font-mono tracking-wide break-all">
                      {r.numero}
                    </span>

                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(
                        dateValue
                      )}
                    </span>

                  </div>


                  <p className="text-sm mt-3 break-words">
                    👤 {client}
                  </p>


                  <p className="text-sm text-gray-600">
                    💳{" "}
                    {r.mode_paiement ||
                      "-"}
                  </p>


                  <p className="text-sm text-gray-600">
                    👨‍💼{" "}
                    {encaisseur}
                  </p>


                  <div className="mt-3 font-bold text-xl text-green-600">
                    {formatMontant(
                      r.montant,
                      r.devise
                    )}
                  </div>

                </div>
              );
            })}

          </div>


          {/* ==================================================
              DESKTOP
          =================================================== */}

          <div className="hidden md:block overflow-x-auto bg-white rounded-2xl shadow">

            <table className="w-full text-sm">

              <thead className="bg-gray-900 text-white">

                <tr>

                  <th className="px-5 py-3 text-left">
                    Date
                  </th>

                  <th className="px-5 py-3 text-left">
                    N° Reçu
                  </th>

                  <th className="px-5 py-3 text-left">
                    Client
                  </th>

                  <th className="px-5 py-3 text-left">
                    Encaissé par
                  </th>

                  <th className="px-5 py-3 text-left">
                    Paiement
                  </th>

                  <th className="px-5 py-3 text-right">
                    Montant
                  </th>

                </tr>

              </thead>


              <tbody>

                {paginated.length === 0 && (
                  <tr>

                    <td
                      colSpan="6"
                      className="px-5 py-8 text-center text-gray-500"
                    >
                      Aucun reçu trouvé.
                    </td>

                  </tr>
                )}


                {paginated.map((r) => {

                  const dateValue =
                    r.date_encaissement ||
                    r.created_at;

                  const client =
                    r.client_nom ||
                    r.client_nom_fiche ||
                    "Client de passage";

                  const encaisseur =
                    r.encaisseur_nom ||
                    "Utilisateur non identifié";

                  return (
                    <tr
                      key={r.id}
                      onClick={() =>
                        openRecu(r.id)
                      }
                      className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer transition"
                    >

                      <td className="px-5 py-3 whitespace-nowrap">

                        <div>
                          {formatDate(
                            dateValue
                          )}
                        </div>

                        <div className="text-xs text-gray-400">
                          {formatHeure(
                            dateValue
                          )}
                        </div>

                      </td>


                      <td className="px-5 py-3 text-green-600 font-semibold font-mono tracking-wide whitespace-nowrap">
                        {r.numero}
                      </td>


                      <td className="px-5 py-3">
                        {client}
                      </td>


                      <td className="px-5 py-3">
                        {encaisseur}
                      </td>


                      <td className="px-5 py-3 capitalize">
                        {r.mode_paiement ||
                          "-"}
                      </td>


                      <td className="px-5 py-3 text-right font-bold whitespace-nowrap">

                        {formatMontant(
                          r.montant,
                          r.devise
                        )}

                      </td>

                    </tr>
                  );
                })}

              </tbody>

            </table>

          </div>


          {/* ==================================================
              PAGINATION
          =================================================== */}

          {filtered.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">

              {/* Informations */}
              <div className="text-sm text-gray-500">
                Affichage de{" "}
                <span className="font-semibold text-gray-700">
                  {(page - 1) * limit + 1}
                </span>
                {" "}à{" "}
                <span className="font-semibold text-gray-700">
                  {Math.min(
                    page * limit,
                    filtered.length
                  )}
                </span>
                {" "}sur{" "}
                <span className="font-semibold text-gray-700">
                  {filtered.length}
                </span>
                {" "}reçu{filtered.length > 1 ? "s" : ""}
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-1">

                {/* Première page */}
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                  className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  «
                </button>

                {/* Précédent */}
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() =>
                    setPage((p) => Math.max(1, p - 1))
                  }
                  className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Précédent
                </button>

                {/* Numéros de pages */}
                {Array.from(
                  { length: totalPages },
                  (_, index) => index + 1
                ).map((numeroPage) => {

                  /*
                   * Si beaucoup de pages, on évite d'afficher
                   * une très longue série de boutons.
                   */
                  if (
                    totalPages > 7 &&
                    numeroPage !== 1 &&
                    numeroPage !== totalPages &&
                    Math.abs(
                      numeroPage - page
                    ) > 2
                  ) {
                    return null;
                  }

                  return (
                    <button
                      type="button"
                      key={numeroPage}
                      onClick={() =>
                        setPage(numeroPage)
                      }
                      className={`min-w-[40px] px-3 py-2 rounded-lg text-sm border transition ${
                        page === numeroPage
                          ? "bg-green-600 text-white border-green-600"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {numeroPage}
                    </button>
                  );
                })}

                {/* Suivant */}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() =>
                    setPage((p) =>
                      Math.min(
                        totalPages,
                        p + 1
                      )
                    )
                  }
                  className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>

                {/* Dernière page */}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() =>
                    setPage(totalPages)
                  }
                  className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  »
                </button>

              </div>

            </div>
          )}

        </>
      )}

    </div>
  );
}